import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Mail, Phone, MapPin, Building2, Pencil, ArrowRightLeft, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DriverFormDialog from "@/components/DriverFormDialog";

const fetcher = (url) => api.get(url).then((r) => r.data);

const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0 gap-4">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm text-brand-text text-right font-medium">{value ?? "—"}</div>
  </div>
);

// Collapsible period group used inside the "Work" tab
const PeriodWorkGroup = ({ group, defaultOpen, canEdit, onEditRow, onTransferRow, onDeleteRow }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="rounded-2xl border-brand-border overflow-hidden" data-testid={`period-group-${group.key}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <div className={`w-6 h-6 rounded-md grid place-items-center transition-transform ${open ? "rotate-90" : ""} text-brand-text-muted`}>
          ▸
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-red font-mono">Pay period</div>
          <div className="font-heading text-base font-bold tracking-tight text-brand-text truncate">{group.period_name}</div>
        </div>
        <div className="hidden sm:flex items-center gap-6 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Days</div>
            <div className="font-heading text-sm font-bold">{group.days}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Stops</div>
            <div className="font-heading text-sm font-bold">{group.stops.toLocaleString("en-GB")}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Parcels</div>
            <div className="font-heading text-sm font-bold">{group.parcels.toLocaleString("en-GB")}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Miles</div>
            <div className="font-heading text-sm font-bold">{Math.round(group.miles).toLocaleString("en-GB")}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Gross</div>
            <div className="font-heading text-sm font-bold text-brand-red">{gbp(group.gross)}</div>
          </div>
        </div>
        <div className="sm:hidden text-right">
          <div className="font-heading text-sm font-bold text-brand-red">{gbp(group.gross)}</div>
          <div className="text-[10px] text-slate-500 font-mono">{group.days}d · {group.stops} stops</div>
        </div>
      </button>
      {open && (
        <div className="border-t border-brand-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Route</th>
                <th className="text-right px-4 py-2.5">Stops</th>
                <th className="text-right px-4 py-2.5">Parcels</th>
                <th className="text-right px-4 py-2.5">Miles</th>
                <th className="text-right px-4 py-2.5">Route pay</th>
                {canEdit && <th className="text-right px-4 py-2.5">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {group.rows.map((r) => (
                <tr key={r.id} data-testid={`work-row-${r.work_date}`}>
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{ddmmyyyy(r.work_date)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.route}</td>
                  <td className="px-4 py-2 text-right">{r.stops}</td>
                  <td className="px-4 py-2 text-right">{r.parcels}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">{r.mileage ? Number(r.mileage).toFixed(1) : "—"}</td>
                  <td className="px-4 py-2 text-right font-heading font-bold">{r.route_payment != null ? gbp(r.route_payment) : "—"}</td>
                  {canEdit && (
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => onEditRow(r)}
                              className="h-7 px-2 text-brand-navy hover:bg-brand-navy/10"
                              data-testid={`edit-row-${r.id}`} title="Edit values">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onTransferRow(r)}
                              className="h-7 px-2 text-brand-red hover:bg-brand-red/10"
                              data-testid={`transfer-row-${r.id}`} title="Transfer">
                        <ArrowRightLeft className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDeleteRow(r)}
                              className="h-7 px-2 text-red-600 hover:bg-red-50"
                              data-testid={`delete-row-${r.id}`} title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const DriverDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: driver, isLoading } = useSWR(`/drivers/${id}`, fetcher);
  const { data: pay } = useSWR(`/drivers/${id}/pay-summary`, fetcher);
  const { data: allWork, mutate: reloadWork } = useSWR(`/drivers/${id}/work-rows`, fetcher);
  const { data: allDrivers } = useSWR("/drivers", fetcher);
  const { data: earnings } = useSWR(`/drivers/${id}/earnings`, fetcher);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // {row, mode: 'edit'|'transfer'}
  const [rowForm, setRowForm] = useState({});
  const [rowBusy, setRowBusy] = useState(false);
  const canEdit = user?.role === "platform_owner" || user?.role === "company_admin";

  const openEditRow = (r) => {
    setEditingRow({ row: r, mode: "edit" });
    setRowForm({
      stops: r.stops ?? 0, parcels: r.parcels ?? 0,
      route: r.route || "",
      route_payment: r.route_payment ?? 0,
    });
  };
  const openTransferRow = (r) => {
    setEditingRow({ row: r, mode: "transfer" });
    setRowForm({ driver_id: "" });
  };
  const saveRow = async () => {
    setRowBusy(true);
    try {
      const body = editingRow.mode === "transfer"
        ? { driver_id: rowForm.driver_id }
        : {
            stops: Number(rowForm.stops) || 0,
            parcels: Number(rowForm.parcels) || 0,
            route: rowForm.route || null,
            route_payment: Number(rowForm.route_payment) || 0,
          };
      await api.patch(`/driver-work-rows/${editingRow.row.id}`, body);
      toast.success(editingRow.mode === "transfer" ? "Row transferred" : "Row updated");
      setEditingRow(null);
      reloadWork();
      mutate(`/drivers/${id}/pay-summary`);
      mutate(`/drivers/${id}/earnings`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setRowBusy(false);
  };
  const deleteRow = async (r) => {
    if (!window.confirm(`Delete work day ${ddmmyyyy(r.work_date)} (${r.route || "no route"})? This cannot be undone.`)) return;
    try {
      await api.delete(`/driver-work-rows/${r.id}`);
      toast.success("Row deleted");
      reloadWork();
      mutate(`/drivers/${id}/pay-summary`);
      mutate(`/drivers/${id}/earnings`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading driver…</div>;
  if (!driver) return <div className="p-6 text-sm text-red-600">Driver not found</div>;

  const summary = pay?.summary;
  const period = pay?.period;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <Link
        to="/admin/drivers"
        data-testid="back-to-drivers"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 hover:text-brand-text transition-colors duration-150"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to drivers
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[var(--brand-navy)] text-white grid place-items-center font-heading text-xl font-bold tracking-tight">
            {driver.full_name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Driver profile</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-brand-text" data-testid="driver-detail-name">{driver.full_name}</h1>
            <div className="text-sm text-slate-500 mt-0.5 font-mono">{driver.driver_code}</div>
          </div>
        </div>
        <Badge variant="outline" className="uppercase tracking-wider text-[10px] self-start md:self-center">
          {(driver.status || "").replace("_", " ")}
        </Badge>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-driver-btn">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit driver
          </Button>
        )}
      </div>

      <DriverFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        driver={driver}
        onSaved={() => { mutate(`/drivers/${id}`); mutate("/drivers"); mutate("/dashboard/kpis"); }}
      />

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white border border-brand-border rounded-xl p-1">
          <TabsTrigger data-testid="tab-overview" value="overview" className="rounded-lg">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-pay" value="pay" className="rounded-lg">Pay</TabsTrigger>
          <TabsTrigger data-testid="tab-work" value="work" className="rounded-lg">Work</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5 rounded-2xl border-brand-border">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Contact</div>
              <div className="space-y-1">
                <InfoRow label={<span className="inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5" />Email</span>} value={driver.email} />
                <InfoRow label={<span className="inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5" />Phone</span>} value={driver.phone} />
                <InfoRow label={<span className="inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" />Depot</span>} value={driver.depot} />
                <InfoRow label={<span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />Vehicle</span>} value={driver.assigned_vehicle} />
              </div>
            </Card>
            <Card className="p-5 rounded-2xl border-brand-border">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Pay configuration</div>
              <div className="space-y-1">
                <InfoRow label="Pay type" value={driver.pay_type?.replace("_", " ")} />
                {driver.pay_type !== "dpd_based" && <InfoRow label="Daily rate" value={gbp(driver.daily_rate)} />}
                {driver.pay_type === "fixed_plus_fees" && (
                  <>
                    <InfoRow label="Vehicle fee /day" value={gbp(driver.vehicle_fee)} />
                    <InfoRow label="Fuel fee /day" value={gbp(driver.fuel_fee)} />
                    <InfoRow label="Management fee /day" value={gbp(driver.management_fee)} />
                  </>
                )}
                <InfoRow label="Insurance /day" value={gbp(driver.insurance_fee)} />
                <InfoRow label="Start date" value={ddmmyyyy(driver.start_date)} />
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Pay */}
        <TabsContent value="pay" className="mt-4">
          {!summary && <div className="text-sm text-slate-500">No pay data.</div>}
          {summary && (
            <Card className="p-5 rounded-2xl border-brand-border">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Current period</div>
                  <div className="font-heading text-lg font-bold tracking-tight">{period?.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Pays on {ddmmyyyy(period?.payment_date)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Net pay</div>
                  <div className="font-heading text-3xl font-bold tracking-tight text-brand-red leading-none" data-testid="driver-pay-net">
                    {gbp(summary.net_pay)}
                  </div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Work days</div><div className="font-heading text-xl font-bold mt-0.5">{summary.days}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Stops</div><div className="font-heading text-xl font-bold mt-0.5">{summary.stops.toLocaleString("en-GB")}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Parcels</div><div className="font-heading text-xl font-bold mt-0.5">{summary.parcels.toLocaleString("en-GB")}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">DPD earnings</div><div className="font-heading text-xl font-bold mt-0.5">{gbp(summary.gross_dpd)}</div></div>
              </div>
              <div className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Calculation breakdown</div>
                <InfoRow label="Base pay" value={gbp(summary.base_pay)} />
                {summary.vehicle_fee_total > 0 && <InfoRow label="Vehicle fee included" value={gbp(summary.vehicle_fee_total)} />}
                {summary.fuel_fee_total > 0 && <InfoRow label="Fuel fee included" value={gbp(summary.fuel_fee_total)} />}
                {summary.management_fee_total > 0 && <InfoRow label="Management fee included" value={gbp(summary.management_fee_total)} />}
                <InfoRow label="Deductions" value={gbp(summary.deductions)} />
                <InfoRow label={<span className="font-bold text-brand-text">Net pay</span>} value={<span className="font-heading font-bold">{gbp(summary.net_pay)}</span>} />
                <InfoRow label="Company gross profit" value={<span className={summary.company_gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}>{gbp(summary.company_gross_profit)}</span>} />
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Work — grouped by pay period, collapsible */}
        <TabsContent value="work" className="mt-4 space-y-3">
          {(() => {
            const rows = allWork?.rows || [];
            // Group by pay_period_id, preserve descending order (newest first)
            const groups = new Map();
            for (const r of rows) {
              const key = r.pay_period_id || "_orphan";
              if (!groups.has(key)) {
                groups.set(key, {
                  key,
                  period_name: r.period_name || "Unassigned",
                  rows: [],
                });
              }
              groups.get(key).rows.push(r);
            }
            const groupList = Array.from(groups.values()).map((g) => {
              const days = g.rows.length;
              const stops = g.rows.reduce((s, r) => s + (r.stops || 0), 0);
              const parcels = g.rows.reduce((s, r) => s + (r.parcels || 0), 0);
              const miles = g.rows.reduce((s, r) => s + (Number(r.mileage) || 0), 0);
              const gross = g.rows.reduce((s, r) => s + (Number(r.route_payment) || 0), 0);
              return { ...g, days, stops, parcels, miles, gross };
            });
            if (groupList.length === 0) {
              return (
                <Card className="rounded-2xl border-brand-border p-8 text-center text-sm text-slate-500">
                  No work rows yet. Trigger a DPD import to populate.
                </Card>
              );
            }
            return groupList.map((g, idx) => (
              <PeriodWorkGroup
                key={g.key}
                group={g}
                defaultOpen={idx === 0}
                canEdit={canEdit}
                onEditRow={openEditRow}
                onTransferRow={openTransferRow}
                onDeleteRow={deleteRow}
              />
            ));
          })()}
        </TabsContent>
      </Tabs>

      {/* Period-by-period earnings breakdown — always visible under tabs */}
      <Card className="rounded-2xl overflow-hidden border-brand-border" data-testid="earnings-history">
        <div className="p-4 border-b border-brand-border flex items-center justify-between">
          <div className="text-sm font-medium">Earnings history — per pay period</div>
          <div className="text-xs text-brand-text-muted">
            {(earnings?.periods || []).length} period(s) with data
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-2.5">Period</th>
                <th className="text-right px-4 py-2.5">Days</th>
                <th className="text-right px-4 py-2.5">Stops</th>
                <th className="text-right px-4 py-2.5">Parcels</th>
                <th className="text-right px-4 py-2.5">Gross</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {(earnings?.periods || []).map((p) => (
                <tr key={p.period_id} data-testid={`earn-row-${p.period_id}`}>
                  <td className="px-4 py-2.5 text-xs">{p.period_name}</td>
                  <td className="px-4 py-2.5 text-right">{p.work_days}</td>
                  <td className="px-4 py-2.5 text-right">{p.stops}</td>
                  <td className="px-4 py-2.5 text-right">{p.parcels}</td>
                  <td className="px-4 py-2.5 text-right font-heading font-bold">{gbp(p.gross_pay)}</td>
                  <td className="px-4 py-2.5 text-xs uppercase tracking-wider">{p.status}</td>
                </tr>
              ))}
              {(earnings?.periods || []).length === 0 && (
                <tr><td colSpan="6" className="text-center py-8 text-slate-500 text-xs">No earnings history yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editingRow} onOpenChange={(o) => { if (!o) setEditingRow(null); }}>
        <DialogContent data-testid="work-row-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingRow?.mode === "transfer" ? "Transfer work row" : "Edit work row"}
            </DialogTitle>
            <DialogDescription>
              {editingRow?.mode === "transfer"
                ? "Move this work day (and its route payment) to a different driver."
                : "Change route, stops, parcels or the DPD route payment for this day."}
              {editingRow?.row && (
                <div className="mt-2 text-xs text-brand-text font-mono">
                  {ddmmyyyy(editingRow.row.work_date)} · {editingRow.row.route || "—"} · {gbp(editingRow.row.route_payment)}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          {editingRow?.mode === "transfer" ? (
            <div className="py-2">
              <Label className="text-xs text-brand-text-muted">Target driver</Label>
              <select
                value={rowForm.driver_id || ""}
                onChange={(e) => setRowForm({ ...rowForm, driver_id: e.target.value })}
                data-testid="transfer-driver-select"
                className="mt-1.5 w-full h-9 rounded-md border border-brand-border bg-white px-2 text-sm"
              >
                <option value="">— Select a driver —</option>
                {(allDrivers || []).filter((d) => d.id !== id).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.driver_code} — {d.full_name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div>
                <Label className="text-xs text-brand-text-muted">Route</Label>
                <Input value={rowForm.route || ""} onChange={(e) => setRowForm({ ...rowForm, route: e.target.value })}
                       data-testid="row-route" className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label className="text-xs text-brand-text-muted">Route payment (£)</Label>
                <Input type="number" step="0.01" value={rowForm.route_payment ?? 0}
                       onChange={(e) => setRowForm({ ...rowForm, route_payment: e.target.value })}
                       data-testid="row-payment" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs text-brand-text-muted">Stops</Label>
                <Input type="number" value={rowForm.stops ?? 0}
                       onChange={(e) => setRowForm({ ...rowForm, stops: e.target.value })}
                       data-testid="row-stops" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs text-brand-text-muted">Parcels</Label>
                <Input type="number" value={rowForm.parcels ?? 0}
                       onChange={(e) => setRowForm({ ...rowForm, parcels: e.target.value })}
                       data-testid="row-parcels" className="mt-1.5" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRow(null)}>Cancel</Button>
            <Button onClick={saveRow}
                    disabled={rowBusy || (editingRow?.mode === "transfer" && !rowForm.driver_id)}
                    className="bg-brand-red hover:bg-brand-red-hover"
                    data-testid="row-save">
              {rowBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {editingRow?.mode === "transfer" ? "Transfer row" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDetail;
